import React from 'react';
import styles from './FundingHistoryV2.module.scss';
import HeaderBodyBox from '../../../../../../common/HeaderBodyBox/HeaderBodyBox';
import dayjs from 'dayjs';
import clsx from 'clsx';
import A4Page from '../../../../../AicaPDFReport/A4Page/A4Page';
import { REPORT_SECTION_NAMES } from '../../../../../../store/pdfReport/pdfReport_constants';
import AIGeneratedBadge from '../../../../../../common/AIGeneratedBadge/AIGeneratedBadge';

interface FundingHistoryItem {
    date: string;
    roundName: string;
    amount: number;
    leadInvestor: string;
    participants: string[];
}

interface FundingHistoryData {
    fundingSummary: string;
    fundingHistory: FundingHistoryItem[];
}

interface FundingHistoryV2Props {
    isPDFReport?: boolean;
    fundingData: FundingHistoryData;
}

const headers = [
    { id: 'date', label: 'Date' },
    { id: 'roundName', label: 'Round' },
    { id: 'amount', label: 'Amount' },
    { id: 'leadInvestor', label: 'Lead' },
    { id: 'participants', label: 'Participants' },
];

function formatParticipants(participants: string[]): React.ReactNode {
    if (!participants.length) return '-';
    if (participants.length <= 1) return participants[0];
    return (
        <>
            {participants[0]} <span className={styles.moreParticipants}>+ {participants.length - 1} more</span>
        </>
    );
}

function formatAmount(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
    }).format(amount);
}

function FundingHistoryV2({ isPDFReport = false, fundingData }: FundingHistoryV2Props) {
    const { fundingSummary, fundingHistory } = fundingData || {};

    const getRowData = (headerId: string, item: FundingHistoryItem, rowIndex: number) => {
        switch (headerId) {
            case 'date':
                return (
                    <div className={styles.rowItem} style={{ color: '#9D9D9D' }}>
                        {item?.date}
                    </div>
                );
            case 'roundName':
                return (
                    <div className={styles.rowItem} style={{ color: '#000000', fontWeight: '700' }}>
                        {item?.roundName}
                    </div>
                );
            case 'participants':
                return <div className={styles.rowItem}>{formatParticipants(item?.participants)}</div>;
            case 'amount':
                return <div className={styles.rowItem}>{item?.amount}</div>;
            default:
                return <div className={styles.rowItem}>{item?.[headerId as keyof FundingHistoryItem]}</div>;
        }
    };

    const render = (
        <div className={styles.fundingHistoryV2}>
            <HeaderBodyBox title="Funding Timeline" isPDFReport={isPDFReport} headerRight={<AIGeneratedBadge />}>
                <div className={styles.content}>
                    <div className={styles.summary}>{fundingSummary}</div>

                    {fundingHistory && fundingHistory.length > 0 && (
                        <div className={styles.table}>
                            <div className={styles.tableHeader}>
                                {headers?.map((header) => (
                                    <div className={clsx(styles.headerItem)} key={header.id}>
                                        {header.label}
                                    </div>
                                ))}
                            </div>
                            <div className={styles.tableBody}>
                                {fundingHistory?.map((item, index) => (
                                    <div className={styles.tableRow} key={index}>
                                        {headers?.map((header) => {
                                            return (
                                                <React.Fragment key={`${index}-${header.id}`}>
                                                    {getRowData(header.id, item, index)}
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </HeaderBodyBox>
        </div>
    );

    if (isPDFReport) {
        return fundingHistory.length ? <A4Page sectionName={REPORT_SECTION_NAMES.FUNDING_HISTORY}>{render}</A4Page> : <></>;
    } else {
        return render;
    }
}

export default FundingHistoryV2;
